import type { ReactElement } from "react";
import type { CatalogField } from "../types.js";
import { maskValue, displayValue } from "../lib/display.js";

interface Props {
  fields: CatalogField[];
  data: Record<string, unknown> | null;
}

/** Generic detail renderer. PII fields are masked; revealing is a separate audited action. */
export function DetailView({ fields, data }: Props): ReactElement {
  if (!data) return <p>No data.</p>;
  return (
    <dl className="detail">
      {fields.map((f) => {
        const raw = data[f.id];
        if (f.type === "list") {
          return (
            <div key={f.id} className="detail-row detail-list">
              <dt>{f.label ?? f.id}</dt>
              <dd>
                <ListTable field={f} rows={raw} />
                {f.description ? <p className="field-hint">{f.description}</p> : null}
              </dd>
            </div>
          );
        }
        const shown = f.pii ? maskValue(f, raw) : displayValue(f, raw);
        return (
          <div key={f.id} className="detail-row">
            <dt>{f.label ?? f.id}</dt>
            <dd>
              {shown}
              {f.pii ? (
                <span className="pii-badge" aria-label="contains personal data">
                  {" "}
                  PII
                </span>
              ) : null}
              {f.description ? <p className="field-hint">{f.description}</p> : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** A `list` field: an array of objects shown as a table, one column per declared column. */
function ListTable({ field, rows }: { field: CatalogField; rows: unknown }): ReactElement {
  const items = Array.isArray(rows) ? rows.filter((r): r is Record<string, unknown> => !!r && typeof r === "object") : [];
  if (items.length === 0) return <span>None</span>;
  const columns = field.columns ?? [];
  return (
    <table>
      <thead>
        <tr>{columns.map((c) => <th key={c.id} scope="col">{c.label ?? c.id}</th>)}</tr>
      </thead>
      <tbody>
        {items.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => <td key={c.id}>{displayValue({ id: c.id, type: c.type, format: c.format }, row[c.id])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
